import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Mail, Plus, Trash2, RefreshCw, CheckCircle2, Clock,
  XCircle, Loader2, Search, ShieldX,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { adminFetch, ConfirmOtpModal } from "./adminUtils";

const STATUS_CONFIG = {
  pending:  { label: "Pendiente",  icon: Clock,        color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  accepted: { label: "Aceptada",   icon: CheckCircle2, color: "text-green-400 bg-green-500/10 border-green-500/20" },
  expired:  { label: "Expirada",   icon: XCircle,      color: "text-muted-foreground bg-slate-700/30 border-slate-700" },
  revoked:  { label: "Revocada",   icon: XCircle,      color: "text-red-400 bg-red-500/10 border-red-500/20" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

const STATUS_LABELS = { pending: "Pendientes", accepted: "Aceptadas", expired: "Expiradas", revoked: "Revocadas" };

export default function AdminInvitations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const fetchedRef = useRef(false);

  // Filtro por URL (?status=pending|accepted|...)
  const urlStatus = searchParams.get("status") || "";

  const loadInvitations = async () => {
    setLoading(true);
    try {
      const data = await adminFetch("/api/admin/invitations");
      setInvitations(data.invitations || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadInvitations();
  }, []); // eslint-disable-line

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await adminFetch("/api/admin/invitations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: name || undefined }),
      });
      toast.success(`Invitación enviada a ${email}`);
      setEmail(""); setName("");
      await loadInvitations();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (invId) => {
    setRevoking(invId);
    try {
      await adminFetch(`/api/admin/invitations/${invId}`, { method: "DELETE" });
      toast.success("Invitación revocada");
      setInvitations((prev) =>
        prev.map((inv) => inv.invitation_id === invId ? { ...inv, status: "revoked" } : inv)
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRevoking(null);
    }
  };

  const handleDeleteRequest = (inv) => {
    setDeleteTarget(inv);
    setShowOtpModal(true);
  };

  const handleDeleteConfirmed = async (sessionId, otp) => {
    await adminFetch(`/api/admin/invitations/${deleteTarget.invitation_id}/permanent`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId, otp }),
    });
    setInvitations((prev) => prev.filter((i) => i.invitation_id !== deleteTarget.invitation_id));
    toast.success("Invitación eliminada permanentemente");
    setDeleteTarget(null);
  };

  // Filtrado combinado: URL status + búsqueda de texto
  const filtered = invitations.filter((inv) => {
    if (urlStatus && inv.status !== urlStatus) return false;
    if (!search) return true;
    return inv.email.toLowerCase().includes(search.toLowerCase()) ||
           (inv.name || "").toLowerCase().includes(search.toLowerCase());
  });

  const filterLabel = urlStatus ? STATUS_LABELS[urlStatus] || urlStatus : "Todas";

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-white text-2xl font-bold">Invitaciones</h1>
          <p className="text-muted-foreground text-sm mt-1">Gestiona el acceso a la plataforma</p>
        </div>
        <button onClick={() => { fetchedRef.current = false; loadInvitations(); }}
          data-testid="admin-refresh-invitations"
          className="flex items-center gap-2 text-muted-foreground hover:text-slate-300 text-sm
                     transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-800">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {/* Send invitation form */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6">
        <h2 className="text-slate-200 font-semibold mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4 text-orange-400" />
          Nueva invitación
        </h2>
        <form onSubmit={handleSend} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Label className="text-muted-foreground text-xs mb-1.5 block">Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@empresa.com" required data-testid="invite-email-input"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 focus:border-orange-500" />
          </div>
          <div className="w-full sm:w-48">
            <Label className="text-muted-foreground text-xs mb-1.5 block">Nombre (opcional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del usuario" data-testid="invite-name-input"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-600 focus:border-orange-500" />
          </div>
          <div className="sm:pt-5">
            <Button type="submit" disabled={sending || !email} data-testid="invite-send-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white w-full sm:w-auto">
              {sending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
                : <><Mail className="w-4 h-4 mr-2" />Enviar invitación</>}
            </Button>
          </div>
        </form>
      </div>

      {/* Filter + Search toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        {/* Status filter pills (from URL) */}
        <div className="flex gap-2 flex-wrap">
          {["", "pending", "accepted", "expired", "revoked"].map((s) => (
            <button key={s}
              onClick={() => { setSearchParams(s ? { status: s } : {}); }}
              data-testid={`filter-${s || "all"}`}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                urlStatus === s
                  ? "bg-orange-500 border-orange-500 text-white"
                  : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"
              }`}>
              {s ? STATUS_LABELS[s] : "Todas"}
            </button>
          ))}
        </div>
        {/* Text search */}
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email..."
            data-testid="invitations-search-input"
            className="pl-9 bg-slate-900 border-slate-700 text-white placeholder:text-slate-600 focus:border-orange-500" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-slate-300 font-semibold text-sm">
            {filterLabel}
            <span className="ml-2 text-muted-foreground font-normal">({filtered.length})</span>
          </h2>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Mail className="w-10 h-10 text-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              {search || urlStatus ? "Sin resultados para este filtro" : "No hay invitaciones todavía"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filtered.map((inv) => (
              <div key={inv.invitation_id} data-testid={`invitation-row-${inv.invitation_id}`}
                className="px-5 py-4 flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                  <span className="text-slate-400 text-xs font-bold uppercase">{inv.email.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-200 text-sm font-medium truncate">{inv.email}</p>
                  {inv.name && <p className="text-muted-foreground text-xs mt-0.5">{inv.name}</p>}
                </div>
                <StatusBadge status={inv.status} />
                <p className="text-muted-foreground text-xs hidden sm:block flex-shrink-0">
                  {new Date(inv.created_at).toLocaleDateString("es-ES",
                    { day: "2-digit", month: "short", year: "numeric" })}
                </p>
                {/* Revocar (solo pendientes) */}
                {inv.status === "pending" && (
                  <button onClick={() => handleRevoke(inv.invitation_id)}
                    disabled={revoking === inv.invitation_id}
                    data-testid={`revoke-btn-${inv.invitation_id}`}
                    title="Revocar invitación"
                    className="text-muted-foreground hover:text-amber-400 transition-colors flex-shrink-0">
                    {revoking === inv.invitation_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <ShieldX className="w-4 h-4" />}
                  </button>
                )}
                {/* Eliminar permanentemente (con OTP) */}
                <button onClick={() => handleDeleteRequest(inv)}
                  data-testid={`delete-inv-btn-${inv.invitation_id}`}
                  title="Eliminar permanentemente"
                  className="text-muted-foreground hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* OTP confirm modal */}
      <ConfirmOtpModal
        open={showOtpModal}
        onClose={() => { setShowOtpModal(false); setDeleteTarget(null); }}
        onConfirmed={handleDeleteConfirmed}
        title="Eliminar invitación"
        description={deleteTarget
          ? `Vas a eliminar permanentemente la invitación de ${deleteTarget.email}. Esta acción no se puede deshacer.`
          : ""}
      />
    </div>
  );
}
